import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { validateDiscountCode, calculateDiscountedPrice } from '../services/discountService';
import { MEMBERSHIP_TIERS } from '../constants/membershipTiers';

const MembershipRegistration = () => {
  const [step, setStep] = useState(1);
  const [selectedTier, setSelectedTier] = useState(null);
  const [discountCode, setDiscountCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    passwordConfirm: '',
    company: '',
    jobTitle: '',
    phone: '',
    linkedinUrl: '',
    aboutMe: ''
  });

  // Legal agreement checkboxes
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [agreedToPrivacy, setAgreedToPrivacy] = useState(false);

  const { signup } = useAuth();
  const navigate = useNavigate();



  // Step 1: Choose Membership Plan
  const handleTierSelection = (tierKey) => {
    setError(''); // Clear any previous errors
    setSelectedTier({ key: tierKey, ...MEMBERSHIP_TIERS[tierKey] });
    setStep(2);
  };

  // Step 2: Fill Profile Information
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Step 3: Apply Discount Code
  const handleDiscountValidation = async () => {
    if (!discountCode.trim()) {
      setAppliedDiscount(null);
      return;
    }

    try {
      setLoading(true);
      const validation = await validateDiscountCode(discountCode.trim());
      
      if (validation.valid) {
        const discountedPrice = calculateDiscountedPrice(
          selectedTier.price,
          validation.discountAmount,
          validation.discountType
        );
        
        setAppliedDiscount({
          code: discountCode.trim().toUpperCase(),
          discount: validation.discount,
          originalPrice: selectedTier.price,
          discountedPrice,
          savings: selectedTier.price - discountedPrice
        });
        setError('');
      } else {
        setAppliedDiscount(null);
        setError(validation.error || 'Invalid discount code');
      }
    } catch (error) {
      setAppliedDiscount(null);
      setError('Error validating discount code');
    } finally {
      setLoading(false);
    }
  };

  // Step 4: Complete Registration & Payment
  const handleRegistration = async () => {
    // Validate form
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.password) {
      setError('Please fill in all required fields');
      return;
    }

    if (formData.password !== formData.passwordConfirm) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    // Validate legal agreements
    if (!agreedToTerms) {
      setError('Please agree to the Terms and Conditions to continue');
      return;
    }

    if (!agreedToPrivacy) {
      setError('Please agree to the Privacy Policy to continue');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Create user account
      const userData = {
        displayName: `${formData.firstName} ${formData.lastName}`,
        firstName: formData.firstName,
        lastName: formData.lastName,
        company: formData.company,
        jobTitle: formData.jobTitle,
        phone: formData.phone,
        linkedinUrl: formData.linkedinUrl,
        aboutMe: formData.aboutMe,
        selectedTier: selectedTier.key,
        discountCode: appliedDiscount?.code || discountCode.trim() || null
      };

      console.log('Starting signup process...');
      console.log('Form data email:', formData.email);
      console.log('User data:', userData);
      const result = await signup(formData.email, formData.password, userData);
      console.log('Signup result:', result);

      // If user was auto-approved (has discount code), redirect to success
      if (result.autoApproved || (appliedDiscount && appliedDiscount.discountedPrice === 0)) {
        console.log('Redirecting to success page (free)');
        navigate('/membership/success?free=true&auto=true');
      } else if (!result.autoApproved) {
        // User needs approval - redirect to pending approval page
        console.log('Redirecting to pending approval page');
        navigate('/pending-approval', {
          state: {
            userData: {
              ...userData,
              email: formData.email,
              selectedTier: selectedTier.key
            }
          }
        });
      } else if (appliedDiscount && appliedDiscount.discountedPrice > 0) {
        // Redirect to payment processing for paid memberships
        console.log('Redirecting to payment page (discounted)');
        navigate('/membership-payment', {
          state: {
            tier: selectedTier,
            discount: appliedDiscount,
            userData: userData
          }
        });
      } else {
        // No discount, full price payment
        console.log('Redirecting to payment page (full price)');
        navigate('/membership-payment', {
          state: {
            tier: selectedTier,
            discount: null,
            userData: userData
          }
        });
      }
    } catch (error) {
      console.error('Registration error:', error);
      setError('Failed to create account: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
      <div className="max-w-6xl mx-auto p-6">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Choose Your Membership</h1>
          <p className="text-lg text-gray-600">Select the membership tier that best fits your career stage</p>
        </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {Object.entries(MEMBERSHIP_TIERS).map(([key, tier]) => (
          <div key={key} className="bg-white rounded-lg shadow-lg p-6 border-2 border-gray-200 hover:border-apn-blue transition-colors">
            <h3 className="text-xl font-bold text-gray-900 mb-2">{tier.name}</h3>
            <div className="text-3xl font-bold text-apn-blue mb-4">${tier.price}/year</div>
            <p className="text-gray-600 mb-6">{tier.description}</p>
            
            <ul className="space-y-2 mb-6">
              {tier.features.map((feature, index) => (
                <li key={index} className="flex items-center text-sm text-gray-600">
                  <svg className="h-4 w-4 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {feature}
                </li>
              ))}
            </ul>

            <button
              onClick={() => handleTierSelection(key)}
              className="w-full bg-apn-blue text-white py-2 px-4 rounded-lg font-semibold hover:bg-blue-700 transition duration-200"
            >
              Select {tier.name}
            </button>
          </div>
        ))}
      </div>



    </div>
  );

  const renderStep2 = () => (
    <div className="max-w-2xl mx-auto p-6">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Complete Your Profile</h1>
        <p className="text-lg text-gray-600">Selected: {selectedTier.name} - ${selectedTier.price}/year</p>
      </div>

      <form className="space-y-6">
        {/* Basic Information */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
            <input
              type="text"
              name="firstName"
              required
              value={formData.firstName}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-apn-blue focus:border-apn-blue"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
            <input
              type="text"
              name="lastName"
              required
              value={formData.lastName}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-apn-blue focus:border-apn-blue"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
          <input
            type="email"
            name="email"
            required
            value={formData.email}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-apn-blue focus:border-apn-blue"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
            <input
              type="password"
              name="password"
              required
              value={formData.password}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-apn-blue focus:border-apn-blue"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password *</label>
            <input
              type="password"
              name="passwordConfirm"
              required
              value={formData.passwordConfirm}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-apn-blue focus:border-apn-blue"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
          <input
            type="text"
            name="company"
            value={formData.company}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-apn-blue focus:border-apn-blue"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Job Title</label>
          <input
            type="text"
            name="jobTitle"
            value={formData.jobTitle}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-apn-blue focus:border-apn-blue"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-apn-blue focus:border-apn-blue"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">LinkedIn Profile</label>
          <input
            type="url"
            name="linkedinUrl"
            value={formData.linkedinUrl}
            onChange={handleInputChange}
            placeholder="https://linkedin.com/in/yourprofile"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-apn-blue focus:border-apn-blue"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">About Me</label>
          <textarea
            name="aboutMe"
            rows={4}
            value={formData.aboutMe}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-apn-blue focus:border-apn-blue"
          />
        </div>

        {/* Discount Code Section */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Discount Code (Optional)</h3>
          <div className="flex space-x-2">
            <input
              type="text"
              value={discountCode}
              onChange={(e) => setDiscountCode(e.target.value)}
              placeholder="Enter discount code"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-apn-blue focus:border-apn-blue"
            />
            <button
              type="button"
              onClick={handleDiscountValidation}
              disabled={loading}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400"
            >
              {loading ? 'Validating...' : 'Apply'}
            </button>
          </div>
          
          {appliedDiscount && (
            <div className="mt-4 p-3 bg-green-100 border border-green-400 rounded-md">
              <p className="text-green-800 font-semibold">✅ Discount Applied!</p>
              <p className="text-green-700">
                {appliedDiscount.discount.description} - Save ${appliedDiscount.savings}
              </p>
              <p className="text-lg font-bold text-green-800">
                Final Price: ${appliedDiscount.discountedPrice}
                {appliedDiscount.discountedPrice === 0 && ' (FREE!)'}
              </p>
            </div>
          )}
        </div>

        {/* Legal Agreements Section */}
        <div className="bg-red-50 border border-red-200 p-6 rounded-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-semibold text-red-800 mb-4">
                ⚠️ LEGAL AGREEMENTS (DEBUG) - Required for All Memberships
              </h3>
              <div className="space-y-3">
                <label className="flex items-start">
                  <input
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    className="mt-1 mr-3 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                    required
                  />
                  <span className="text-sm text-gray-700">
                    I agree to the{' '}
                    <Link
                      to="/legal/terms-and-conditions"
                      target="_blank"
                      className="text-blue-600 hover:text-blue-800 underline"
                    >
                      Terms and Conditions
                    </Link>
                    {' '}*
                  </span>
                </label>

                <label className="flex items-start">
                  <input
                    type="checkbox"
                    checked={agreedToPrivacy}
                    onChange={(e) => setAgreedToPrivacy(e.target.checked)}
                    className="mt-1 mr-3 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                    required
                  />
                  <span className="text-sm text-gray-700">
                    I agree to the{' '}
                    <Link
                      to="/legal/privacy-policy"
                      target="_blank"
                      className="text-blue-600 hover:text-blue-800 underline"
                    >
                      Privacy Policy
                    </Link>
                    {' '}*
                  </span>
                </label>
              </div>
              <p className="text-xs text-red-600 mt-2 font-medium">
                * Required for membership registration
              </p>
            </div>
          </div>
        </div>

        {error && (
          <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded-md">
            {error}
          </div>
        )}

        <div className="flex space-x-4">
          <button
            type="button"
            onClick={() => setStep(1)}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
          >
            Back to Plans
          </button>
          <button
            type="button"
            onClick={handleRegistration}
            disabled={loading || !agreedToTerms || !agreedToPrivacy}
            className="flex-1 px-4 py-2 bg-apn-blue text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {loading ? 'Creating Account...' :
             appliedDiscount && appliedDiscount.discountedPrice === 0 ? 'Complete Registration (FREE)' :
             'Continue to Payment'}
          </button>
        </div>

        {(!agreedToTerms || !agreedToPrivacy) && (
          <p className="text-sm text-red-600 text-center">
            Please agree to both the Terms and Conditions and Privacy Policy to continue
          </p>
        )}
      </form>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
    </div>
  );
};

export default MembershipRegistration;
